import React, { useCallback, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { HomePage } from '../components/HomePage';
import { FavoritesSection } from '../components/favorites/FavoritesSection';
import { Footer } from '../components/layout/Footer';
import { LivechatFrame } from '../components/LivechatFrame';
import { ScrollToTop } from '../components/ScrollToTop';
import { useQuery } from '@tanstack/react-query';
import { configClient } from '../lib/supabase/configClient';
import { SocialNetworksModal } from '../components/socialnetworks/SocialNetworksModal';
import { SectionRouter } from './SectionRouter';
import { LoadingIndicator } from '../components/shared/LoadingIndicator';

export const AppRoutes: React.FC = () => {
  const [livechatUrl, setLivechatUrl] = useState<string | null>(null);
  const [showLivechat, setShowLivechat] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showSocialNetworks, setShowSocialNetworks] = useState(false);

  const { data: config, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: () => configClient.getConfig(),
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  const handleLivechatOpen = useCallback((url: string) => {
    if (url !== livechatUrl) setLivechatUrl(url);
    setShowLivechat(true);
    setMessageCount(0);
  }, [livechatUrl]);

  const handleLivechatClose = useCallback(() => {
    setShowLivechat(false);
  }, []);

  const handleLivechatMessage = useCallback(() => {
    if (!showLivechat) {
      setMessageCount(prev => prev + 1);
    }
  }, [showLivechat]);

  const handleSocialNetworksOpen = useCallback(() => {
    setShowSocialNetworks(true);
  }, []);

  const handleSocialNetworksClose = useCallback(() => {
    setShowSocialNetworks(false);
  }, []);

  if (isLoading) return <LoadingIndicator id="app-routes-loader" />;

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-red-600">
        <h2 className="text-xl font-semibold mb-2">Une erreur est survenue</h2>
        <p className="text-center mb-4">Erreur lors du chargement de la configuration</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (!config) return <LoadingIndicator id="app-routes-no-config-loader" />;

  return (
    <>
      <ScrollToTop />

     <Routes>
      {/* Homepage */}
      <Route
        path="/"
        element={
          <HomePage
            onLivechatClick={handleLivechatOpen}
            onSocialNetworksClick={handleSocialNetworksOpen}
            messageCount={messageCount}
          />
        }
      />

      {/* Favorites - direct component route */}
      <Route path="/favoris" element={<FavoritesSection />} />

      {/* Sections : /:slug or /:slug/:name */}
      <Route path="/:slug" element={<SectionRouter />} />
      <Route path="/:slug/:name" element={<SectionRouter />} />
      <Route path="/:slug/:name/:item" element={<SectionRouter />} />

      {/* Unknown = redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

      <Footer config={config} />

      {livechatUrl && (
        <LivechatFrame
          url={livechatUrl}
          onClose={handleLivechatClose}
          onMessage={handleLivechatMessage}
          config={config}
          visible={showLivechat}
        />
      )}

      {config && (
        <SocialNetworksModal
          isOpen={showSocialNetworks}
          onClose={handleSocialNetworksClose}
          config={config}
          translations={{}}
        />
      )}
    </>
  );
};